import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR, formatArea, formatPricePerSqft } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import CallbackButton from "@/components/CallbackButton";
import SEO, { propertyJsonLd } from "@/components/SEO";
import PaidReportBlock from "@/components/PaidReportBlock";
import { ShieldCheck, MapPin, BedDouble, Bath, Maximize2, Star, Scale, Activity, TrendingUp, Building2, Phone, MessageCircle, Heart, GitCompare, FileCheck, ChevronLeft } from "lucide-react";

// Static decorative rows for the blurred paid-report cards (hoisted to avoid re-creation per render)
const TRUST_LOCATION_FIELDS = [
  { label: "Connectivity Score", blurValue: "•• / 10" },
  { label: "Road Access (verified)", blurValue: "•••• ft" },
  { label: "Nearby Verified Landmarks", blurValue: "•••••••••••••••" },
  { label: "Future Appreciation View", blurValue: "•••••••••••••" },
];
const TRUST_LEGAL_FIELDS = [
  { label: "Title Status", blurValue: "••••••••••" },
  { label: "Ownership Verified", blurValue: "•••" },
  { label: "Encumbrance", blurValue: "•••••••" },
  { label: "Mutation / Tax Status", blurValue: "••••••••••" },
];
const FAIR_PRICE_FIELDS = [
  { label: "Fair Price (Low)", blurValue: "₹ ••,••,•••" },
  { label: "Fair Price (High)", blurValue: "₹ ••,••,•••" },
  { label: "Comparable Deals Reviewed", blurValue: "••" },
];

function isEmbeddable(url) {
  return /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
}
function toEmbed(url) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}
function formatChargeValue(type, value) {
  if (!value) return "—";
  const raw = String(value).trim();
  if (type === "value") {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? formatINR(n) : raw;
  }
  if (type === "percent") {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? `${n}%` : raw;
  }
  return raw; // free-form text
}

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const { user, promptLogin, refresh } = useAuth();
  const liked = !!p && (user?.liked_properties || []).includes(p.id);
  const [likeBusy, setLikeBusy] = useState(false);

  useEffect(() => {
    api.get(`/property/${id}`).catch(() => null);
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [id]);

  const toggleLike = async () => {
    if (!p) return;
    if (!user) { toast.message("Sign in to save properties"); promptLogin(); return; }
    if (likeBusy) return;
    setLikeBusy(true);
    try {
      if (liked) await api.delete(`/my/likes/${p.id}`);
      else await api.post(`/my/likes/${p.id}`);
      await refresh();
      toast.success(liked ? "Removed from saved" : "Saved to your list");
    } catch { toast.error("Failed to update saved list"); }
    finally { setLikeBusy(false); }
  };

  if (!p) return <div className="container-tharlo py-20 text-center text-muted-foreground">Loading property...</div>;

  return (
    <div data-testid="property-detail-page">
      <SEO
        title={p.title}
        description={`${p.title} — ${p.locality}, ${p.city}. ${formatINR(p.price)} · ${formatArea(p.area_value, p.area_unit)}. ${p.description?.slice(0, 140)}`}
        image={p.images?.[0]}
        type="product"
        jsonLd={propertyJsonLd(p)}
      />
      {/* Breadcrumb */}
      <div className="container-tharlo pt-6">
        <Link to="/properties" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-ink">
          <ChevronLeft className="w-4 h-4" /> All Properties
        </Link>
      </div>

      {/* Header */}
      <div className="container-tharlo pt-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {p.is_sold && <span className="trust-badge bg-red-600 text-white border-red-600">Sold Out</span>}
          {p.verified && !p.is_sold && <span className="trust-badge bg-gold text-white border-gold"><ShieldCheck className="w-3 h-3" /> Verified by Tharlo</span>}
          {p.featured && !p.is_sold && <span className="trust-badge bg-ink text-white border-ink"><Star className="w-3 h-3" /> Premium</span>}
          <span className="trust-badge">{p.property_type}</span>
          <span className="trust-badge">For {p.listing_type}</span>
        </div>
        <h1 className="font-display text-3xl lg:text-5xl text-ink tracking-tight">{p.title}</h1>
        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4 text-gold" /> {p.address || `${p.locality}, ${p.city}`}
          <span className="text-xs ml-3">ID: {p.id.slice(0, 8).toUpperCase()}</span>
        </div>
      </div>

      <div className="container-tharlo pt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left - Gallery + details */}
        <div className="lg:col-span-8">
          <div className="aspect-[16/10] bg-muted overflow-hidden">
            <img src={p.images?.[activeImg] || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=80"} alt={p.title} className="w-full h-full object-cover" />
          </div>
          {p.images?.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {p.images.map((src, i) => (
                <button key={`${src}-${i}`} onClick={() => setActiveImg(i)} className={`aspect-[4/3] overflow-hidden ${i === activeImg ? "ring-2 ring-gold" : ""}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {p.video_url && (
            <div className="mt-6" data-testid="property-video">
              <div className="overline mb-3">Property Video</div>
              {isEmbeddable(p.video_url) ? (
                <div className="aspect-video bg-black">
                  <iframe src={toEmbed(p.video_url)} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title="Property video" />
                </div>
              ) : (
                <video src={p.video_url} controls className="w-full aspect-video bg-black" />
              )}
            </div>
          )}

          {/* Quick stats */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 grid-spec">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Area</div>
              <div className="mt-1 font-display text-xl text-ink">{formatArea(p.area_value, p.area_unit)}</div>
            </div>
            {p.bedrooms > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Bedrooms</div>
                <div className="mt-1 font-display text-xl text-ink">{p.bedrooms}</div>
              </div>
            )}
            {p.bathrooms > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Washrooms</div>
                <div className="mt-1 font-display text-xl text-ink">{p.bathrooms}</div>
              </div>
            )}
            {p.road_width_ft && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Road Width</div>
                <div className="mt-1 font-display text-xl text-ink">{p.road_width_ft} ft</div>
              </div>
            )}
          </div>

          {/* Residential amenities */}
          {p.property_type === "residential" && (p.gated_society != null || p.gas_pipeline != null || p.electricity_connection != null || p.water_availability != null || p.age_of_property_years != null) && (
            <div className="mt-8">
              <h3 className="font-display text-xl text-ink mb-4">Amenities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {p.gated_society != null && <AmenityChip label="Gated Society" yes={p.gated_society} />}
                {p.gas_pipeline != null && <AmenityChip label="Gas Pipeline" yes={p.gas_pipeline} />}
                {p.electricity_connection != null && <AmenityChip label="Electricity Connection" yes={p.electricity_connection} />}
                {p.water_availability != null && <AmenityChip label="Water Availability" yes={p.water_availability} />}
                {p.age_of_property_years != null && <AmenityChip label={`Age: ${p.age_of_property_years} yr${p.age_of_property_years===1?"":"s"}`} yes={true} />}
              </div>
            </div>
          )}

          {/* Agricultural details */}
          {p.property_type === "agricultural" && (p.irrigated != null || p.direct_road_access != null) && (
            <div className="mt-8">
              <h3 className="font-display text-xl text-ink mb-4">Agricultural Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {p.irrigated != null && <AmenityChip label={p.irrigated ? `Irrigated${p.irrigation_type ? ` (${p.irrigation_type})` : ""}` : "Not Irrigated"} yes={p.irrigated} />}
                {p.direct_road_access != null && <AmenityChip label="Direct Road Access" yes={p.direct_road_access} />}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mt-10">
            <h2 className="font-display text-2xl text-ink mb-4">About this Property</h2>
            <p className="text-foreground/80 leading-relaxed">{p.description}</p>
          </div>

          {/* As-per-Seller Property Info */}
          <div className="mt-10 bg-white border border-border p-6 lg:p-8" data-testid="seller-info-section">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="font-display text-2xl text-ink">Property Information</h2>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">As per seller</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoRow icon={<Scale className="w-4 h-4" />} label="Legal Status" value={p.legal_status || "Not specified"} />
              <InfoRow icon={<Building2 className="w-4 h-4" />} label="Nearby Infrastructure" value={(p.nearby || []).slice(0,3).join(" · ") || "Not specified"} />
              <InfoRow icon={<MapPin className="w-4 h-4" />} label="Road Width" value={p.road_width_ft ? `${p.road_width_ft} ft` : "Not specified"} />
              <InfoRow icon={<TrendingUp className="w-4 h-4" />} label="Seller's Note on Appreciation" value={p.appreciation_potential || "Not specified"} />
            </div>
            <p className="mt-5 text-[11px] text-muted-foreground border-t border-border pt-4">
              The details above are shared by the seller. Tharlo has not independently verified this information — for a Tharlo-verified report, unlock the sections below.
            </p>
          </div>

          {/* Tharlo Trust Score — PAID, blurred until user requests unlock */}
          <div className="mt-10" data-testid="tharlo-trust-section">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="overline text-gold">Tharlo Trust Score</div>
                <h2 className="font-display text-3xl text-ink mt-1">Independently verified by Tharlo</h2>
              </div>
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl mb-6">
              These reports are prepared by Tharlo's ground team and legal panel — separate from what the seller has claimed. Two focus areas, each priced individually.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <PaidReportBlock
                propertyId={p.id}
                report="trust_location"
                title="Location & Info Verification"
                subtitle="Physical inspection, connectivity score, road access, nearby-infrastructure survey and future-appreciation view."
                fields={TRUST_LOCATION_FIELDS}
                variant="dark"
              />
              <PaidReportBlock
                propertyId={p.id}
                report="trust_legal"
                title="Legal Verification (Docs Check)"
                subtitle="Title verification, ownership check, encumbrance certificate review, mutation and tax status."
                fields={TRUST_LEGAL_FIELDS}
                variant="dark"
              />
            </div>
          </div>

          {/* Pricing — Fair Price Range is blurred paid report */}
          <div className="mt-10 bg-white border border-border p-6 lg:p-8">
            <div className="overline mb-3">Pricing</div>
            <h2 className="font-display text-2xl text-ink mb-6">Transparent Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Seller Asking Price</div>
                <div className="mt-2 font-display text-3xl text-ink">{formatINR(p.price)}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">As per seller</div>
              </div>
              <PaidReportBlock
                propertyId={p.id}
                report="fair_price"
                title="Tharlo Fair Price Range"
                subtitle="Data-driven range derived from comparable transactions, locality trends and Tharlo's on-ground insights."
                fields={FAIR_PRICE_FIELDS}
                variant="light"
              />
            </div>
            <div className="mt-6 pt-5 border-t border-border">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Coming Soon</div>
              <div className="mt-1 text-sm text-foreground/70">Price discovery &amp; bidding</div>
            </div>
          </div>
        </div>

        {/* Right - Contact */}
        <aside className="lg:col-span-4">
          <div className="bg-white border border-border p-6 sticky top-24">
            <div className="font-display text-2xl text-ink mb-1">
              {formatINR(p.price)}
              {p.listing_type === "rent" && <span className="text-sm text-muted-foreground font-body ml-2">/ month</span>}
            </div>
            {(() => {
              const rate = formatPricePerSqft(p.price, p.area_value, p.area_unit, p.listing_type);
              return rate ? <div className="text-xs text-muted-foreground mb-3" data-testid="price-per-sqft">{rate}</div> : null;
            })()}
            {(p.brokerage_value || p.security_deposit_value) && (
              <div className="mt-3 mb-4 space-y-1.5 text-xs" data-testid="rent-extras">
                {p.brokerage_value && (
                  <div className="flex items-center justify-between gap-2 border-b border-dashed border-border pb-1.5">
                    <span className="text-muted-foreground">Brokerage</span>
                    <strong className="text-ink text-right" data-testid="rent-brokerage">{formatChargeValue(p.brokerage_type, p.brokerage_value)}</strong>
                  </div>
                )}
                {p.security_deposit_value && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Security Deposit</span>
                    <strong className="text-ink text-right" data-testid="rent-deposit">{formatChargeValue(p.security_deposit_type, p.security_deposit_value)}</strong>
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground mb-5">All inquiries go directly to Tharlo&apos;s advisors.</p>
            <CallbackButton propertyId={p.id} propertyTitle={p.title} />
            <div className="mt-4 grid grid-cols-2 gap-2">
              <a href={`https://wa.me/919294662384?text=Interested in ${encodeURIComponent(p.title)}`} target="_blank" rel="noreferrer" data-testid="action-whatsapp" className="border border-border hover:border-gold hover:text-gold p-3 text-xs font-semibold flex items-center justify-center gap-1.5">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href="tel:+919294662384" data-testid="action-call" className="border border-border hover:border-gold hover:text-gold p-3 text-xs font-semibold flex items-center justify-center gap-1.5">
                <Phone className="w-4 h-4" /> Call
              </a>
              <button onClick={toggleLike} data-testid="action-save" aria-pressed={liked} className={`border p-3 text-xs font-semibold flex items-center justify-center gap-1.5 ${liked ? "bg-red-500 text-white border-red-500" : "border-border hover:border-gold hover:text-gold"}`}>
                <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {liked ? "Saved" : "Save"}
              </button>
              <button data-testid="action-compare" className="border border-border hover:border-gold hover:text-gold p-3 text-xs font-semibold flex items-center justify-center gap-1.5">
                <GitCompare className="w-4 h-4" /> Compare
              </button>
            </div>
            <div className="mt-5 pt-5 border-t border-border flex items-center gap-2 text-xs text-foreground/70">
              <FileCheck className="w-4 h-4 text-gold" /> Need legal verification? <span className="text-muted-foreground italic ml-1">Coming soon</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function TrustItem({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-white/5">
      <div className="w-9 h-9 bg-gold/20 text-gold flex items-center justify-center shrink-0">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</div>
        <div className="mt-1 font-semibold">{value}</div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 bg-gold/10 text-gold flex items-center justify-center shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-1 text-sm text-ink font-semibold break-words">{value}</div>
      </div>
    </div>
  );
}

function AmenityChip({ label, yes }) {
  return (
    <div className={`flex items-center gap-2 px-4 py-2.5 border ${yes ? "border-gold/40 bg-gold/5" : "border-border bg-muted/30"}`}>
      <span className={`w-2 h-2 ${yes ? "bg-gold" : "bg-muted-foreground/40"}`} />
      <span className={`text-sm ${yes ? "text-ink font-medium" : "text-muted-foreground line-through"}`}>{label}</span>
    </div>
  );
}
